import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type Mode = "manager" | "operator";

type ModeContextValue = {
  mode: Mode;
  toggleMode: () => void;
};

const ModeContext = createContext<ModeContextValue | null>(null);

const STORAGE_KEY = "compostdeveloper_mode";

export function ModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>(() => {
    if (window.location.pathname.startsWith("/app/operator")) return "operator";
    const stored = localStorage.getItem(STORAGE_KEY) as Mode | null;
    return stored === "operator" ? "operator" : "manager";
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    const nextMode = location.pathname.startsWith("/app/operator") ? "operator" : "manager";
    setMode((current) => (current === nextMode ? current : nextMode));
  }, [location.pathname]);

  const toggleMode = useCallback(() => {
    const next = mode === "manager" ? "operator" : "manager";
    setMode(next);
    // Redirect after state update to avoid updating router during render
    if (location.pathname.startsWith("/app")) {
      if (next === "operator") {
        navigate("/app/operator");
      } else {
        navigate("/app");
      }
    }
  }, [mode, navigate, location.pathname]);

  return (
    <ModeContext.Provider value={{ mode, toggleMode }}>
      {children}
    </ModeContext.Provider>
  );
}

export function useMode() {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used within ModeProvider");
  return ctx;
}
